<template>
  <div
    class="container page article-page"
    :class="{ 'delivery-cut-off-date-page': isDeliveryCutOffDatePage }"
  >
    <div v-if="pageBody && !loading" v-html="pageBody"></div>
    <div v-else-if="loading" class="article-loading">
      <sib-skeleton-loading height="15em"></sib-skeleton-loading>
      <section class="section">
        <h2 class="pb-lg"><sib-skeleton-loading></sib-skeleton-loading></h2>
        <p>
          <sib-skeleton-loading></sib-skeleton-loading>
          <sib-skeleton-loading></sib-skeleton-loading>
          <sib-skeleton-loading width="60%"></sib-skeleton-loading>
        </p>
        <p>
          <sib-skeleton-loading></sib-skeleton-loading>
          <sib-skeleton-loading width="30%"></sib-skeleton-loading>
        </p>
        <p>
          <sib-skeleton-loading width="80%"></sib-skeleton-loading>
        </p>
      </section>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from '@vue/reactivity'
import { onServerPrefetch, onMounted, onUnmounted, useSSRContext } from 'vue'
import { useRoute } from 'vue-router'
import usePageStore from '@/store/page'
import { usePage } from '@/composables/page'
import { useFetchData } from '@/composables/fetchData'
import { NullOrType, Page } from '@/types'
import { isOnServer } from '@/utils/ssr'
import { appendToHead } from '@/composables/appendToHead'
import {
  SSR_CONTEXT_APPEND_TO_HEAD_META_TAGS,
  QUERY_STRING_NO_CACHE,
} from '@/utils/constants'
import CacheService from '@/services/cache'
import Logger from '@/services/log'
import { injectScript, injectStyle } from '@/utils/dom'

const route = useRoute()
const pageStore = usePageStore()
const pageHandle = computed(() => route.params.handle || '')
const pageBody = computed(() => pageStore.page?.body)
const pageTitle = computed(() => pageStore.page?.title)
const { setPageTitle, setPageDescription } = usePage()

const { fetchData, loading, resetVariables } = useFetchData({
  fetchDataFunction: fetchPage,
  checkNotFoundFunction: checkNotFound,
  setNotFoundFunction: setPageNotFound,
})

const ssrContext = isOnServer && useSSRContext()

const isDeliveryCutOffDatePage = computed(() =>
  route.params.handle.includes('delivery-cut-off-date')
)

onServerPrefetch(async () => {
  await fetchData()
  // reset variables to prevent hydration mismatch in case can not fetch data.
  // we will try to load again on the client
  resetVariables()
  if (pageTitle.value) setPageTitle(pageTitle.value.toString())

  if (pageBody.value) {
    const description = getPageDescription(pageBody.value)
    setPageDescription(description)
  } else {
    return
  }

  // inject meta tags into head tag
  if (ssrContext) {
    appendToHead(
      `<meta property="og:type" content="article" />`,
      SSR_CONTEXT_APPEND_TO_HEAD_META_TAGS,
      ssrContext
    )
  }
})

onMounted(async () => {
  if (pageStore.isPageNotFound) return
  if (!pageStore.page) {
    await fetchData()
  } else {
    if (pageStore.page.__cacheKey) {
      Logger.log('Article Page', 'Set page from server side to cache', 3)
      // save page from server to local cache
      CacheService.instance?.set(
        pageStore.page.__cacheKey,
        JSON.parse(JSON.stringify(pageStore.page))
      )
    }
  }

  // after fetching data, check the page is not found again
  if (pageStore.isPageNotFound) {
    return
  }

  handleArticleTab()
  injectFreshdesk()
})

onUnmounted(() => {
  pageStore.resetPageState()
})

async function fetchPage() {
  if (pageHandle.value) {
    return await pageStore.getPageByHandle({
      handle: pageHandle.value.toString(),
      isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
    })
  }
  return null
}

function checkNotFound(page: NullOrType<Page>) {
  return !page
}

function setPageNotFound(isNotFound: boolean) {
  pageStore.isPageNotFound = isNotFound
}

function handleArticleTab() {
  const articleTabElems = document.querySelectorAll('.article-tab__item')
  const articleResultElems = document.querySelectorAll('.article-result__item')
  articleTabElems.forEach((elm) =>
    clickArticleTab(articleTabElems, articleResultElems, elm)
  )
}

function clickArticleTab(
  tabElms: NodeListOf<Element>,
  resultElms: NodeListOf<Element>,
  tabElm: Element
) {
  tabElm.addEventListener('click', function () {
    //style for tab element
    tabElms.forEach((tabElm) => tabElm.classList.remove('active'))
    tabElm.classList.add('active')

    //handle hide or show tab result
    resultElms.forEach((elm) => {
      elm.classList.remove('d-block')
    })
    const datasetTabElm = tabElm.getAttribute('data-article-tab')
    const articleResultElm: NullOrType<Element> = document.querySelector(
      `.${datasetTabElm}`
    )
    articleResultElm?.classList.add('d-block')
    window.scrollTo(0, 200)
  })
}

function getPageDescription(pageBody: NullOrType<string>) {
  if (!pageBody) return
  const removeEleHtmlRegex = /<[^>]+>/g
  const removeSpacingRegex = /\s+/g

  // remove beginning of string to banner of page
  const index = pageBody.indexOf('</header>')
  pageBody = pageBody.substring(index, pageBody.length)

  // get words from html string
  let description = pageBody
    .replace(removeEleHtmlRegex, ' ')
    .replace(removeSpacingRegex, ' ')
    .trim()

  // get 300 characters.
  description = description.substring(0, 300)

  // take from the beginning of the string to the last dot
  if (!description.endsWith('.')) {
    const index = description.lastIndexOf('.')
    description = description.substring(0, index)
  }

  return description
}

function injectFreshdesk() {
  const FRESHDESK_STYLE_HREF =
    'https://s3.amazonaws.com/assets.freshdesk.com/widget/freshwidget.css'
  const FRESHDESK_SCRIPT_SRC =
    'https://s3.amazonaws.com/assets.freshdesk.com/widget/freshwidget.js'
  const FEEDBACK_FORM_IFRAME_ID = 'freshwidget-embedded-form'

  const feedbackFormIframe = document.querySelector(
    `iframe[id='${FEEDBACK_FORM_IFRAME_ID}']`
  )
  if (!feedbackFormIframe) return

  const freshdeskStyle = document.querySelector(
    `link[href='${FRESHDESK_STYLE_HREF}']`
  )
  const freshdeskScript = document.querySelector(
    `script[src='${FRESHDESK_SCRIPT_SRC}']`
  )

  if (!freshdeskStyle) injectStyle(FRESHDESK_STYLE_HREF)
  if (!freshdeskScript) injectScript(FRESHDESK_SCRIPT_SRC, undefined, true)
}
</script>

<style lang="scss">
.article-page {
  h2 {
    font-weight: 700;
    font-size: 2.45rem;
    line-height: 1.2;
    margin: 0 auto 0.5rem;
    color: #000;
  }
  p {
    font-size: 1rem;
    line-height: 1.57;
    margin: 0 auto 1em;
    color: #000;
  }
  .article-header {
    position: relative;
    padding: 2em;
    z-index: 1;
    @include media-md-up {
      padding: 3em;
    }
    @include media-xl-up {
      padding: 4em;
    }
    @include media-xxl-up {
      padding: 5em;
    }
    h1 {
      color: var(--color-primary);
      margin-bottom: var(--space-large);
      margin-top: 0;
    }
    &-background {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -1;
      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
      }
    }
  }
  .section {
    padding: 2.28em 0;
    @include media-lg-up {
      padding: 5.7em 0;
    }
  }
  .box {
    padding: 1.7em;
    margin-bottom: 2.28em;
    box-sizing: border-box;
    @include media-lg-up {
      padding: 3.2em 4.4em;
    }
    &--primary {
      background: var(--color-primary);
    }
  }
  .hero-text {
    width: unset;
    color: #fff;
    font-size: 2rem;
    margin: 0;
    @include media-md-up {
      font-size: 2.4rem;
    }
    &--w-lg-10em {
      @include media-lg-up {
        width: 10em;
      }
    }
  }
  .image-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    &--mxh-22em {
      max-height: 22rem;
    }
  }
  .article-card {
    &__header {
      background: var(--background-color);
      padding: 0.75em 1.15em;
      margin-bottom: 2em;
    }
    &__heading {
      font-weight: 700;
      font-size: 1.5rem;
      color: #000;
      margin: 0;
      user-select: none;
    }
    &__body {
      margin: 2em 0;
    }
    input {
      display: none;
    }
    input:checked + .article-card__body {
      display: none;
    }
  }
  .article-arrow {
    list-style: none;
    position: relative;
    font-size: 1rem;
    padding-left: 1.5em;
    line-height: 1.5;
    color: #000;
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.57em;
      height: 0.35em;
      width: 0.35em;
      border: 0.14em solid #000;
      border-width: 0.15em 0.15em 0 0;
      transform: rotate(45deg);
    }
  }
  .text-color-secondary {
    color: var(--color-secondary);
  }
  .flex-align-center {
    display: flex;
    align-items: center;
  }
  .article-tab {
    &__item {
      background-color: var(--background-color);
      text-align: center;
      padding: 1em;
      font-weight: 600;
      font-size: 1rem;
      text-transform: uppercase;
      cursor: pointer;
      user-select: none;
      &:hover {
        text-decoration: underline;
      }
    }
    .active {
      background-color: var(--background-color-primary);
    }
  }
  .article-result__item {
    display: none;
  }
  .d-block {
    display: block;
  }
  .fw-400 {
    font-weight: 400;
  }
  .mailto {
    color: var(--color-primary);
  }
}
.delivery-cut-off-date-page {
  table {
    tr,
    th,
    td {
      width: auto !important;
      height: auto !important;
      max-width: unset !important;
      max-height: unset !important;
    }
    th,
    td {
      padding: 0.5rem;
    }
  }
}
</style>
